import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type User, updateProfileSchema } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useTranslation } from "react-i18next";

import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, CreditCard, Building, Settings, Bell, Download, Rows3, LayoutGrid } from "lucide-react";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { ChangePlanModal } from "@/components/change-plan-modal";
import { useDensity } from "@/hooks/use-density";
import { BillingSection } from "@/components/billing-section";
// TwoFactorAuthToggle, SecurityAuditLog - dočasne skryté
import { PWAInstallButton, PWAMobileInstructions } from "@/components/pwa-install-button";
import { Check } from "lucide-react";

// Zoznam krajín pre select (oficiálne anglické názvy, rovnaký ako v client-form.tsx)
const COUNTRIES = [
  // EÚ krajiny
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  // Mimo EÚ
  'Albania',
  'Argentina',
  'Australia',
  'Belarus',
  'Bosnia and Herzegovina',
  'Brazil',
  'Canada',
  'China',
  'Hong Kong',
  'India',
  'Israel',
  'Japan',
  'Kosovo',
  'Mexico',
  'Moldova',
  'Montenegro',
  'New Zealand',
  'North Macedonia',
  'Norway',
  'Russia',
  'Saudi Arabia',
  'Serbia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Switzerland',
  'Turkey',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
].sort();

function DensitySettingCard() {
  const { density, setDensity, isCompact } = useDensity();
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium mb-2">Hustota rozhrania</h4>
        <p className="text-sm text-muted-foreground mb-4">
          Compact zobrazí viac riadkov v tabuľkách (Úlohy, Faktúry). Comfortable má väčšie rozostupy.
        </p>
        <div className="flex gap-2">
          <Button
            variant={!isCompact ? "default" : "outline"}
            size="sm"
            onClick={() => setDensity("comfortable")}
            className="flex items-center gap-2"
          >
            <LayoutGrid className="h-4 w-4" />
            Comfortable
          </Button>
          <Button
            variant={isCompact ? "default" : "outline"}
            size="sm"
            onClick={() => setDensity("compact")}
            className="flex items-center gap-2"
          >
            <Rows3 className="h-4 w-4" />
            Compact
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['profile', 'common']);
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
  
  // Reminder settings
  const [fabEnabled, setFabEnabled] = useState(() => {
    const saved = localStorage.getItem('reminder-fab-enabled');
    return saved ? JSON.parse(saved) : true;
  });
  
  
  const handleFabToggle = (enabled: boolean) => {
    setFabEnabled(enabled);
    localStorage.setItem('reminder-fab-enabled', JSON.stringify(enabled));
  };
  


  const form = useForm({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      companyName: user?.companyName || "",
      companyStreet: user?.companyStreet || "",
      companyCity: user?.companyCity || "",
      companyPostalCode: user?.companyPostalCode || "",
      companyCountry: user?.companyCountry || "Slovakia",
      companyRegNumber: user?.companyRegNumber || "",
      companyTaxId: user?.companyTaxId || "",
      companyVatId: user?.companyVatId || "",
      companyVatRate: user?.companyVatRate ? Number(user.companyVatRate) : 23,
      isVatPayer: user?.isVatPayer === true || (user?.isVatPayer === undefined && !!user?.companyVatId),

      bankAccount: user?.bankAccount || "",
      bankIban: user?.bankIban || "",
      bankSwift: user?.bankSwift || "",
      
      // Invoice customization settings
      invoiceNumberFormat: user?.invoiceNumberFormat || "{year}-INV-{sequence}",
      invoiceFooterText: user?.invoiceFooterText || "",
      invoicePrimaryColor: user?.invoicePrimaryColor || "#059669", // Default green color
      invoiceStartingNumber: user?.invoiceStartingNumber || 1,
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: Partial<User>) => {
      console.log('🔧 [Profile] Updating profile with data:', data);
      const res = await apiRequest("PATCH", "/api/user/profile", data);
      const result = await res.json();
      console.log('🔧 [Profile] Update response:', result);
      return result;
    },
    onSuccess: () => {
      console.log('🔧 [Profile] Update successful');
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: t('notifications:updated'),
        description: t('notifications:profileUpdated'),
      });
    },
    onError: (error: Error) => {
      console.error('🔧 [Profile] Update error:', error);
      toast({
        title: t('notifications:info'),
        description: error.message,
        variant: "default",
      });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ type, file }: { type: 'logo' | 'stamp', file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const res = await apiRequest("POST", "/api/user/upload", formData);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: t('notifications:uploadSuccessful'),
        description: t('notifications:uploadSuccessDescription'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:uploadFailed'),
        description: error.message,
        variant: "default",
      });
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: async (type: 'logo' | 'stamp') => {
      const res = await apiRequest("DELETE", `/api/user/upload/${type}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: t('notifications:deleted'),
        description: t('notifications:assetRemoved'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('notifications:deleteFailed'),
        description: error.message,
        variant: "default",
      });
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    // The schema handling will take care of proper conversion of VAT rate
    const updatedData = {
      ...data,
      // Respect user's choice for isVatPayer regardless of VAT ID
      isVatPayer: data.isVatPayer,
      // Pass VAT rate as a number, the schema will handle conversion
      companyVatRate: data.companyVatRate
    };
    
    updateProfileMutation.mutate(updatedData as any);
  });

  return (
    <div className="page-container min-w-0 overflow-x-hidden">
      <div className="flex-1 space-y-6 sm:space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 section-spacing">
          <div>
            <h1 className="page-title">Firemný profil</h1>
            <p className="text-muted-foreground text-lg">Spravujte údaje vašej spoločnosti a nastavenia</p>
          </div>
        </div>

        <Tabs defaultValue="company" className="w-full min-w-0">
          <TabsList className="justify-start w-full overflow-x-auto flex-nowrap h-auto gap-0.5 p-1 -mx-1 sm:mx-0">
            <TabsTrigger value="company" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Building className="h-4 w-4" />
              <span>Firemné údaje</span>
            </TabsTrigger>
            <TabsTrigger value="subscription" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <CreditCard className="h-4 w-4" />
              <span>Predplatné</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <CreditCard className="h-4 w-4" />
              <span>Moje platby</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Settings className="h-4 w-4" />
              <span>Bezpečnosť</span>
            </TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Bell className="h-4 w-4" />
              <span>Pripomienky</span>
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Rows3 className="h-4 w-4" />
              <span>Vzhľad</span>
            </TabsTrigger>
            <TabsTrigger value="export" className="flex items-center gap-2 text-sm whitespace-nowrap">
              <Download className="h-4 w-4" />
              <span>Export dát</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="company" className="space-y-8 mt-6">
          {/* Firemný profil sekcia */}
          <Card>
            <CardHeader>
              <CardTitle>Firemné údaje</CardTitle>
              <CardDescription>
                Základné informácie o vašej firme používané pri fakturácii
              </CardDescription>
            </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-4">
                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('companyName')}</FormLabel>
                            <FormControl>
                              <Input {...field} className="w-full max-w-xl" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 xs:grid-cols-1 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="companyStreet"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('street')}</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyCity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('city')}</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyPostalCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('postalCode')}</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyCountry"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('country')}</FormLabel>
                              <Select
                                value={field.value || "Slovakia"}
                                onValueChange={field.onChange}
                              >
                              <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Vyberte krajinu" />
                                  </SelectTrigger>
                              </FormControl>
                                <SelectContent>
                                  {COUNTRIES.map((country) => (
                                    <SelectItem key={country} value={country}>
                                      {country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid gap-4 xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="companyRegNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('regNumber')}</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyTaxId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('taxId')}</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="companyVatId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('vatId')}</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    
                                    // When VAT ID is filled, suggest setting isVatPayer to true
                                    // But don't auto-uncheck it when VAT ID is empty
                                    if (e.target.value && !form.getValues('isVatPayer')) {
                                      form.setValue('isVatPayer', true);
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="isVatPayer"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>{t('vatPayer')}</FormLabel>
                                <FormDescription>
                                  {t('vatPayerDescription')}
                                </FormDescription>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="companyVatRate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('vatRate')}</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  value={field.value || 23} 
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? '' : Number(e.target.value);
                                    field.onChange(value);
                                  }}
                                  min={0} 
                                  max={100} 
                                  step={0.1}
                                />
                              </FormControl>
                              <FormDescription>
                                {t('vatRateDescription')}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="ml-auto"
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('common:buttons.saveChanges', 'Uložiť zmeny')
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('bankDetails')}</CardTitle>
                <CardDescription>
                  {t('bankDetailsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-4">
                      <FormField
                        control={form.control}
                        name="bankAccount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('accountNumber')}</FormLabel>
                            <FormControl>
                              <Input {...field} className="w-full max-w-xl" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bankIban"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('iban')}</FormLabel>
                            <FormControl>
                              <Input {...field} className="w-full max-w-xl" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bankSwift"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('swiftBic')}</FormLabel>
                            <FormControl>
                              <Input {...field} className="w-full max-w-xl" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="ml-auto"
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('common:buttons.saveChanges', 'Uložiť zmeny')
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Nastavenia faktúr</CardTitle>
                <CardDescription>
                  Prispôsobte formát číslovania faktúr a pridajte vlastný text do päty
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-4">
                      <FormField
                        control={form.control}
                        name="invoiceNumberFormat"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Formát čísla faktúry</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                className="max-w-xl"
                                placeholder="{year}-INV-{sequence}"
                              />
                            </FormControl>
                            <FormDescription>
                              Použite {"{year}"} pre rok, {"{month}"} pre mesiac a {"{sequence}"} pre poradové číslo.
                              Príklad: {"{year}-INV-{sequence}"} vytvorí 2025-INV-001
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="invoicePrimaryColor"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Primárna farba faktúr</FormLabel>
                            <FormDescription>
                              Môžete použiť jednu farbu alebo CSS gradient ako: linear-gradient(45deg, #ff6b6b, #4ecdc4)
                            </FormDescription>
                            <FormControl>
                              <div className="flex items-center gap-3">
                                <Input 
                                  type="color"
                                  {...field}
                                  className="w-16 h-10 p-1 border-2"
                                />
                                <Input 
                                  type="text"
                                  {...field}
                                  className="flex-1 max-w-md"
                                  placeholder="Napríklad: linear-gradient(45deg, #ff6b6b, #4ecdc4)"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="invoiceStartingNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Počiatočné číslo faktúry</FormLabel>
                            <FormControl>
                              <Input 
                                type="number"
                                min="1"
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                className="max-w-[150px]"
                                placeholder="1"
                              />
                            </FormControl>
                            <FormDescription>
                              Nastavte od akého čísla majú začínať nové faktúry. Napríklad: 1 = 001, 100 = 100, 1000 = 1000
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="invoiceFooterText"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Text v päte faktúry</FormLabel>
                            <FormControl>
                              <textarea 
                                {...field}
                                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 max-w-xl"
                                placeholder="Napríklad: Ďakujeme za dôveru v naše služby. Pre akékoľvek otázky nás neváhajte kontaktovať."
                              />
                            </FormControl>
                            <FormDescription>
                              Tento text sa zobrazí v spodnej časti faktúry (voliteľné)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="ml-auto"
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('common:buttons.saveChanges', 'Uložiť zmeny')
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('companyAssets')}</CardTitle>
                <CardDescription>
                  {t('companyAssetsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="logo">{t('companyLogo')}</Label>
                    <div className="mt-2 space-y-4">
                      {user?.companyLogo && (
                        <>
                          <div className="rounded-lg border p-2 w-32 h-32 flex items-center justify-center">
                            <img
                              src={user.companyLogo}
                              alt={t('companyLogo')}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="mt-2 text-destructive"
                            onClick={() => deleteMutation.mutate('logo')}
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending && deleteMutation.variables === 'logo' ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : null}
                            {t('removeLogo')}
                          </Button>
                        </>
                      )}
                      <Input
                        id="logo"
                        type="file"
                        accept="image/*"
                        disabled={uploadMutation.isPending}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadMutation.mutate({ type: 'logo', file });
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="stamp">{t('companyStamp')}</Label>
                    <div className="mt-2 space-y-4">
                      {user?.companyStamp && (
                        <>
                          <div className="rounded-lg border p-2 w-32 h-32 flex items-center justify-center">
                            <img
                              src={user.companyStamp}
                              alt={t('companyStamp')}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="mt-2 text-destructive"
                            onClick={() => deleteMutation.mutate('stamp')}
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending && deleteMutation.variables === 'stamp' ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : null}
                            Odstrániť pečiatku
                          </Button>
                        </>
                      )}
                      <Input
                        id="stamp"
                        type="file"
                        accept="image/*"
                        disabled={uploadMutation.isPending}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadMutation.mutate({ type: 'stamp', file });
                        }}
                      />
                    </div>
                  </div>
                </div>


              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription" className="space-y-8 mt-6">
            {/* Predplatné sekcia - zjednodušené */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Zmeniť predplatný plán
                </CardTitle>
                <CardDescription>
                  Vyberte si nový predplatný plán, ktorý najlepšie vyhovuje vašim potrebám
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Aktuálny plán</p>
                    <p className="text-lg font-semibold">{user?.plan || "Zadarmo"}</p>
                  </div>
                  <Button 
                    onClick={() => setIsChangePlanModalOpen(true)}
                    className="w-full sm:w-auto"
                  >
                    Zmeniť plán
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-8 mt-6">
            {/* Billing sekcia - obsahuje detailné info o predplatnom a históriu platieb */}
            <BillingSection />
          </TabsContent>

          <TabsContent value="security" className="space-y-6 mt-6">
            {/* 2FA a Security audit logy dočasne skryté - nefungujú / sú v EN */}
            {/* <TwoFactorAuthToggle /> */}
            {/* <SecurityAuditLog /> */}
            
            {/* PWA Installation Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Mobilná aplikácia
                </CardTitle>
                <CardDescription>
                  Nainštalujte TaskKrow ako aplikáciu na vašom zariadení pre lepší zážitok
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <PWAInstallButton variant="default" size="lg" className="w-full" />
                <PWAMobileInstructions />
                <div className="space-y-2 pt-4 border-t">
                  <p className="text-sm font-medium">Ako nainštalovať:</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">iOS (iPhone/iPad):</p>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside ml-2">
                        <li>Otvorte Safari (nie Chrome)</li>
                        <li>Kliknite na tlačidlo Zdieľať (štvorec s šípkou)</li>
                        <li>Vyberte "Pridať na domovskú obrazovku"</li>
                      </ol>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Android:</p>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside ml-2">
                        <li>Otvorte Chrome</li>
                        <li>Kliknite na menu (3 bodky vpravo hore)</li>
                        <li>Vyberte "Pridať na domovskú obrazovku" alebo "Inštalovať aplikáciu"</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reminders" className="space-y-8 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.title')}</CardTitle>
                <CardDescription>
                  Nastavenia pre rýchle pripomienky a notifikácie
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* FAB Settings */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h4 className="text-sm font-medium">{t('settings.enableFab')}</h4>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.fabDescription')}
                      </p>
                    </div>
                    <Checkbox
                      checked={fabEnabled}
                      onCheckedChange={handleFabToggle}
                    />
                  </div>
                </div>

                {/* Push notifikácie dočasne skryté - nefungujú */}
                {/* {pushSupported && (...)} */}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="appearance" className="space-y-8 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Vzhľad</CardTitle>
                <CardDescription>
                  Nastavenia hustoty rozhrania pre tabuľky a zoznamy
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DensitySettingCard />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="export" className="space-y-8 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export dát
                </CardTitle>
                <CardDescription>
                  Exportujte vaše faktúry, úlohy a klientov do rôznych formátov
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-3">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/export/invoices', {
                          method: 'GET',
                          credentials: 'include',
                          headers: {
                            'Accept': 'application/zip',
                          },
                        });
                        
                        if (!response.ok) {
                          if (response.status === 401) {
                            throw new Error('Nie ste prihlásený');
                          }
                          const errorData = await response.json().catch(() => ({ error: 'Export sa nepodaril' }));
                          throw new Error(errorData.error || 'Export sa nepodaril');
                        }
                        
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.style.display = 'none';
                        a.href = url;
                        a.download = `faktury-${new Date().toISOString().split('T')[0]}.zip`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        
                        toast({
                          title: "Export dokončený",
                          description: "Faktúry boli exportované ako ZIP súbor",
                        });
                      } catch (error) {
                        console.error('Export error:', error);
                        toast({
                          title: "Chyba exportu",
                          description: error instanceof Error ? error.message : "Nepodarilo sa exportovať faktúry",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <div className="font-semibold">Stiahnuť faktúry (ZIP)</div>
                      <div className="text-xs text-muted-foreground mt-1">Export všetkých faktúr ako PDF v ZIP súbore</div>
                    </div>
                  </Button>

                  <Button 
                    variant="outline" 
                    size="lg"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/export/tasks', {
                          method: 'GET',
                          credentials: 'include',
                          headers: {
                            'Accept': 'text/csv',
                          },
                        });
                        
                        if (!response.ok) {
                          if (response.status === 401) {
                            throw new Error('Nie ste prihlásený');
                          }
                          throw new Error('Export sa nepodaril');
                        }
                        
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.style.display = 'none';
                        a.href = url;
                        a.download = `ulohy-${new Date().toISOString().split('T')[0]}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        
                        toast({
                          title: "Export dokončený",
                          description: "Úlohy boli exportované ako CSV súbor",
                        });
                      } catch (error) {
                        console.error('Export error:', error);
                        toast({
                          title: "Chyba exportu",
                          description: error instanceof Error ? error.message : "Nepodarilo sa exportovať úlohy",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <div className="font-semibold">Export úloh (CSV)</div>
                      <div className="text-xs text-muted-foreground mt-1">Export všetkých úloh do CSV formátu</div>
                    </div>
                  </Button>

                  <Button 
                    variant="outline" 
                    size="lg"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/export/clients', {
                          method: 'GET',
                          credentials: 'include',
                          headers: {
                            'Accept': 'text/csv',
                          },
                        });
                        
                        if (!response.ok) {
                          if (response.status === 401) {
                            throw new Error('Nie ste prihlásený');
                          }
                          throw new Error('Export sa nepodaril');
                        }
                        
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.style.display = 'none';
                        a.href = url;
                        a.download = `klienti-${new Date().toISOString().split('T')[0]}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        
                        toast({
                          title: "Export dokončený",
                          description: "Klienti boli exportovaní ako CSV súbor",
                        });
                      } catch (error) {
                        console.error('Export error:', error);
                        toast({
                          title: "Chyba exportu",
                          description: error instanceof Error ? error.message : "Nepodarilo sa exportovať klientov",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <div className="font-semibold">Export klientov (CSV)</div>
                      <div className="text-xs text-muted-foreground mt-1">Export všetkých klientov do CSV formátu</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Change Plan Modal */}
        <ChangePlanModal
          isOpen={isChangePlanModalOpen}
          onClose={() => setIsChangePlanModalOpen(false)}
          currentPlan={user?.plan}
          currentUserEmail={user?.email}
          locale="sk"
        />
      </div>
    </div>
  );
}